import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import DocumentVerify from '@/common/icons/DocumentVerify.svg';

import { StepCard, InitializationStatus, InitializationError } from '@/components/step-card';

import { EVENT } from '@/common/constants';
import { useEventAgent } from '@/common/hooks/useEventAgent';
import { useStepper } from '@/components/stepper';
import { useNotification } from '@/components/notification';
import { useFaceAuth } from '@/common/hooks/useFaceAuth';

import { CircleCheckIcon } from './circle-check-icon';

function RegisterFaceId() {
  const { t } = useTranslation();
  const hasTrackedPageView = useRef(false);
  const hasTrackedSDKError = useRef(false);
  const { trackEventWithDedup } = useEventAgent();
  const { setError } = useNotification();
  const { onStepClick } = useStepper();
  const {
    doEnrollment,
    isInitialized,
    isInitializing,
    error: faceAuthError,
    retryInitialization,
    initializationAttempts,
    lastFailureReason,
    getDiagnosticInfo,
  } = useFaceAuth({
    callback: (result) => {
      if (result?.status === 0) {
        if (result?.isCompletelyDone) {
          trackEventWithDedup(EVENT.FACIAL_RECOGNITION_SUCCESS_PAGE_VIEW);
          onStepClick(1);
        } else {
          trackEventWithDedup(EVENT.FACE_RECOGNITION_ERROR, { status: result?.status });
          setError('try-again', () => {
            window.location.reload();
          });
        }
      } else {
        if (result?.status === 12) {
          trackEventWithDedup(EVENT.CAMERA_PERMISSION_ERROR);
          trackEventWithDedup(EVENT.NO_ACCESS_CAMERA_BUTTON_CLICKED);
        } else if (result?.status === 18) {
          trackEventWithDedup(EVENT.NETWORK_ERROR);
        } else if (result?.status === 2) {
          trackEventWithDedup(EVENT.FACE_LIVENESS_ERROR);
        } else {
          trackEventWithDedup(EVENT.FIRST_STEP_ERROR_VIEW, { status: result?.status });
        }
        setError('try-again');
      }
    },
  });

  const handleOpenCamera = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    trackEventWithDedup(EVENT.START_FIRST_STEP_BUTTON_CLICKED);
    doEnrollment();
  };

  useEffect(() => {
    if (!hasTrackedPageView.current) {
      trackEventWithDedup(EVENT.START_FACIAL_RECOGNITION_PAGE_VIEW);
      hasTrackedPageView.current = true;
    }

    if (faceAuthError && !hasTrackedSDKError.current) {
      trackEventWithDedup(EVENT.SDK_INITIALIZATION_ERROR);
      hasTrackedSDKError.current = true;
    }
  }, [faceAuthError, trackEventWithDedup]);

  const handleRetry = () => {
    if (retryInitialization) {
      retryInitialization();
    } else {
      window.location.reload();
    }
  };

  // Handle diagnostic info for developers
  const handleShowDiagnostics = () => {
    if (getDiagnosticInfo) {
      const diagnostics = getDiagnosticInfo();

      // Show in console for developers
      console.group('🔍 FaceTec Initialization Diagnostics');
      console.log('Full Diagnostic Report:', diagnostics);
      console.log('Failure Reason:', lastFailureReason);
      console.log('Attempts:', initializationAttempts);
      console.groupEnd();

      // Could also show in UI modal or copy to clipboard
      if (navigator.clipboard) {
        navigator.clipboard
          .writeText(JSON.stringify(diagnostics, null, 2))
          .then(() => alert('Diagnostic info copied to clipboard'))
          .catch(() => console.log('Could not copy to clipboard'));
      }
    }
  };

  return (
    <StepCard
      icon={DocumentVerify}
      title={t('introFlow.title')}
      buttonText={t('introFlow.buttonText')}
      onButtonClick={handleOpenCamera}
      loadingButton={(isInitializing || !isInitialized) && initializationAttempts < 3}
      disabledButton={isInitializing || !isInitialized || !!faceAuthError}
    >
      <>
        <p className="mb-4">{t('introFlow.tips.header')}</p>
        <ul>
          <li className="mb-4 flex items-center">
            <CircleCheckIcon /> {t('introFlow.tips.tip1')}
          </li>
          <li className="mb-4 flex items-center">
            <CircleCheckIcon /> {t('introFlow.tips.tip2')}
          </li>
          <li className="mb-4 flex items-center">
            <CircleCheckIcon /> {t('introFlow.tips.tip3')}
          </li>
        </ul>

        {faceAuthError && (
          <InitializationError
            title="Initialization Failed"
            lastFailureReason={lastFailureReason ?? undefined}
            initializationAttempts={initializationAttempts}
            onRetry={handleRetry}
            onShowDiagnostics={handleShowDiagnostics}
            showDiagnosticsButton={process.env.NODE_ENV === 'development'}
            solutions={[
              'Refresh the page and try again',
              'Check your internet connection',
              'Use a supported browser (Chrome, Firefox, Safari, Edge)',
              'Enable camera permissions in browser settings',
            ]}
          />
        )}

        <InitializationStatus
          isInitializing={isInitializing}
          initializationAttempts={initializationAttempts}
          message={t('introFlow.initializingMessage', 'Initializing facial verification system...')}
        />
      </>
    </StepCard>
  );
}

export default RegisterFaceId;
